/**
 * The contract handed to an LLM that is asked to write a lesson deck.
 *
 * The point of this module is that the contract is *derived* from the code that
 * enforces it — `ALLOWED_BLOCK_TYPES` from the deck validator and
 * `WORKSHEET_QUESTION_TYPES` from the worksheet module — rather than restated by
 * hand. The previous hand-written copy in templateUtils.js had drifted badly:
 * it listed 14 of the 25 supported block types, declared `video` and `image`
 * unsupported when both are supported, and documented worksheet question types
 * (`shortAnswer`, `multipleChoice`) that had been renamed two schema versions
 * earlier. `assertContractIsComplete` below fails a test if that happens again.
 */
import { ALLOWED_BLOCK_TYPES } from './boppps/validateDeck';
import { deckStyleContractText } from './deckStyles';
import { WORKSHEET_QUESTION_TYPES } from './worksheetQuestionTypes';

import { DECK_FORMAT, DECK_VERSION, DECK_SCHEMA_TYPE } from './deckContractConstants';

export { DECK_FORMAT, DECK_VERSION, DECK_SCHEMA_TYPE };

/** The BOPPPS sections a deck is built from, in running order. */
export const BOPPPS_SECTIONS = [
  { id: 'bridge-in', title: 'Bridge-In', purpose: 'Hook attention and connect to what learners already know' },
  { id: 'outcomes', title: 'Learning Outcomes', purpose: 'What learners will be able to do by the end' },
  { id: 'pre-assessment', title: 'Pre-Assessment', purpose: 'Surface existing knowledge and misconceptions' },
  { id: 'participatory-learning', title: 'Participatory Learning', purpose: 'The main teaching and activity slides' },
  { id: 'post-assessment', title: 'Post-Assessment', purpose: 'Check the outcomes were met' },
  { id: 'summary', title: 'Summary', purpose: 'Key takeaways and what comes next' },
  { id: 'resources', title: 'Resources', purpose: 'Materials, references and further reading' }
];

export const BOPPPS_SECTION_IDS = BOPPPS_SECTIONS.map((section) => section.id);

/**
 * How to fill each block type. Keyed by the type string the validator accepts.
 * `shape` is the minimum an LLM must emit; `repairDeck` supplies ids, the
 * empty tail arrays and every styling default, so nothing else is required.
 */
export const BLOCK_TYPE_SPECS = {
  headline: { shape: '{ "type": "headline", "slideId": "...", "content": "<h2>Slide title</h2>" }', use: 'The title line of a slide. One per slide, first.' },
  heading: { shape: '{ "type": "heading", "slideId": "...", "content": "<h3>Sub-heading</h3>" }', use: 'A sub-heading inside a slide.' },
  text: { shape: '{ "type": "text", "slideId": "...", "content": "<p>Prose.</p>" }', use: 'Paragraphs. Keep to 2–4 sentences per block.' },
  list: { shape: '{ "type": "list", "slideId": "...", "content": "<ul><li>Point</li></ul>" }', use: 'Bulleted or numbered points. 3–6 bullets reads best on a slide.' },
  cards: { shape: '{ "type": "cards", "slideId": "...", "items": [{ "title": "Concept", "content": "<p>Explanation.</p>" }] }', use: 'A grid of short concepts or definitions. 2–4 items.' },
  'stacked-cards': { shape: '{ "type": "stacked-cards", "slideId": "...", "items": [{ "title": "Step 1", "content": "<p>What to do.</p>", "sideText": "5 min" }] }', use: 'Ordered steps, stages or a timeline.' },
  'multi-column-cards': { shape: '{ "type": "multi-column-cards", "slideId": "...", "columns": [{ "title": "Column", "width": "50%", "widthPercent": 50, "cards": [{ "title": "Row", "content": "<p>Cell.</p>" }] }] }', use: 'Comparisons across 2–4 categories. This is how you express a table.' },
  accordion: { shape: '{ "type": "accordion", "slideId": "...", "accordionItems": [{ "title": "Question?", "content": "<p>Answer.</p>" }] }', use: 'FAQ-style expandable items.' },
  'info-box': { shape: '{ "type": "info-box", "slideId": "...", "title": "Key point", "content": "<p>Worth remembering.</p>" }', use: 'A highlighted aside.' },
  'warning-box': { shape: '{ "type": "warning-box", "slideId": "...", "title": "Careful", "content": "<p>Common mistake.</p>" }', use: 'A caution or common pitfall.' },
  'exercise-box': { shape: '{ "type": "exercise-box", "slideId": "...", "title": "Try it", "content": "<p>Hands-on task.</p>" }', use: 'An activity learners do now.' },
  'project-timeline': { shape: '{ "type": "project-timeline", "slideId": "...", "items": [{ "title": "Week 1", "content": "<p>Milestone.</p>" }] }', use: 'Project or assessment milestones.' },
  divider: { shape: '{ "type": "divider", "slideId": "...", "content": { "text": "" } }', use: 'A visual break. `content` must be an object, not a string.' },
  worksheet: { shape: '{ "type": "worksheet", "slideId": "...", "title": "Check-in", "questions": [{ "type": "short-answer", "title": "Question text", "points": 2 }] }', use: 'Graded or ungraded questions. See the question types below.' },
  image: { shape: '{ "type": "image", "slideId": "...", "src": "", "alt": "Describe the image" }', use: 'Leave `src` empty — the instructor adds the picture in the app. Write the `alt` text so they know what to find.' },
  gallery: { shape: '{ "type": "gallery", "slideId": "...", "items": [{ "src": "", "caption": "What this shows" }] }', use: 'Several images. Same rule: captions yes, `src` empty.' },
  video: { shape: '{ "type": "video", "slideId": "...", "videoUrl": "https://www.youtube.com/watch?v=...", "title": "Why this clip" }', use: 'A single embedded video. Only use a URL you are certain exists.' },
  'youtube-playlist': { shape: '{ "type": "youtube-playlist", "slideId": "...", "videoUrl": "https://www.youtube.com/playlist?list=...", "title": "Playlist" }', use: 'A YouTube playlist embed.' },
  audio: { shape: '{ "type": "audio", "slideId": "...", "audioUrl": "", "title": "Clip name" }', use: 'Audio. Leave `audioUrl` empty for the instructor to fill.' },
  link: { shape: '{ "type": "link", "slideId": "...", "href": "https://example.com", "title": "Link text" }', use: 'An external link. Only real URLs.' },
  'apa-quote': { shape: '{ "type": "apa-quote", "slideId": "...", "content": "<p>Quoted passage.</p>", "citation": "Author (2024)" }', use: 'A pull quote with attribution.' },
  'apa-citation': { shape: '{ "type": "apa-citation", "slideId": "...", "citations": [{ "type": "book", "author": "Author, A. A.", "year": "2024", "title": "Title", "source": "Publisher" }] }', use: 'An in-text citation. Only cite sources you are certain exist.' },
  'apa-reference-list': { shape: '{ "type": "apa-reference-list", "slideId": "...", "citations": [] }', use: 'The reference list. Leave `citations` empty — the app collects them from the apa-citation blocks.' },
  html: { shape: '{ "type": "html", "slideId": "...", "content": "<div>...</div>" }', use: 'Raw HTML. Prefer a real block type; this one is a last resort.' },
  'embedded-html': { shape: '{ "type": "embedded-html", "slideId": "...", "content": "<div>...</div>" }', use: 'A self-contained HTML layout, rendered in a sandboxed frame.' },
  'custom-html': { shape: '{ "type": "custom-html", "slideId": "...", "html": "<section class=\\"lp-slide\\">...</section>", "css": ".lp-slide { display: grid; }", "cssScope": "slide" }', use: 'A hand-written layout for the body of one slide. Markup goes on `html` and styles on `css` — NOT on `content`. `cssScope` is "slide" (rules are prefixed with this block\'s selector) or "deck" (left global). No scripts, iframes or form controls; size in %, fr, cqw and em so the layout holds at 16:9.' },
  image_drawover: { shape: '{ "type": "image_drawover", "slideId": "...", "content": "" }', use: 'A picture the instructor draws on top of in the app. Leave `content` empty — it is filled in by the shape builder, not by you.' }
};

/** Block types the validator rejects outright, with what to use instead. */
export const REJECTED_BLOCK_TYPES = {
  table: 'Use multi-column-cards (columns) or stacked-cards (rows).',
  chart: 'Describe the data with cards or a list; the instructor adds the chart.',
  quote: 'Use apa-quote, or info-box if there is no source to cite.',
  code: 'Use html, or a text block containing <pre><code>.'
};

/**
 * Guards against the drift this module exists to prevent. Called by the tests:
 * every type the validator accepts must be documented here, and nothing may be
 * documented that the validator would reject.
 */
export const assertContractIsComplete = () => {
  const documented = Object.keys(BLOCK_TYPE_SPECS);
  const undocumented = ALLOWED_BLOCK_TYPES.filter((type) => !documented.includes(type));
  const invented = documented.filter((type) => !ALLOWED_BLOCK_TYPES.includes(type));
  const contradictory = Object.keys(REJECTED_BLOCK_TYPES).filter((type) => ALLOWED_BLOCK_TYPES.includes(type));
  return { undocumented, invented, contradictory };
};

const bullet = (line) => `- ${line}`;

const blockTypeLines = () => ALLOWED_BLOCK_TYPES
  .filter((type) => BLOCK_TYPE_SPECS[type])
  .map((type) => `${bullet(`**${type}** — ${BLOCK_TYPE_SPECS[type].use}`)}\n  ${BLOCK_TYPE_SPECS[type].shape}`)
  .join('\n');

const worksheetTypeLines = () => WORKSHEET_QUESTION_TYPES
  .map(({ value, label, hint }) => bullet(`\`${value}\` — ${label}${hint ? ` (${hint})` : ''}`))
  .join('\n');

const rejectedLines = () => Object.entries(REJECTED_BLOCK_TYPES)
  .map(([type, advice]) => bullet(`\`${type}\` — ${advice}`))
  .join('\n');

/**
 * The schema half of the prompt: what a valid deck looks like and which pieces
 * the app fills in for you. Kept separate from the lesson content so it can be
 * reviewed on its own.
 */
export const buildSchemaContractText = ({ deckStyle, customDeck } = {}) => `## Output format

Return **one JSON object and nothing else** — no prose before or after, no markdown fence.

\`\`\`json
{
  "format": "${DECK_FORMAT}",
  "version": "${DECK_VERSION}",
  "schema_type": "${DECK_SCHEMA_TYPE}",
  "courseTopic": "Lesson title",
  "sections": {
    "bridge-in": {
      "id": "bridge-in",
      "title": "Bridge-In",
      "blocks": [
        { "type": "headline", "slideId": "bridge-in-1", "content": "<h2>First slide title</h2>" },
        { "type": "text", "slideId": "bridge-in-1", "content": "<p>Body copy.</p>" },
        { "type": "divider", "slideId": "bridge-in-2", "content": { "isSlideBreak": true } },
        { "type": "headline", "slideId": "bridge-in-2", "content": "<h2>Second slide title</h2>" },
        { "type": "list", "slideId": "bridge-in-2", "content": "<ul><li>A point</li></ul>" }
      ]
    }
  }
}
\`\`\`

### Do not emit these — the app generates them
\`id\` on blocks and items, \`timestamp\`, \`slideNames\`, \`slideVisibility\`, the empty
\`items\`/\`cardItems\`/\`stackedCardItems\`/\`accordionItems\`/\`questions\` arrays, and every
colour, footer and title-card default. Adding them is not an error, but getting them
wrong is — leave them out.

${deckStyleContractText(deckStyle, customDeck)}

### Slides — this is the part decks get wrong
Two things together make a slide, and **both are required**:

1. **\`slideId\`** names the slide a block belongs to. Every block carrying the same
   \`slideId\` renders together, in order. Name them \`<section-id>-<n>\`, for example
   \`participatory-learning-3\`.
2. **A slide break ends it.** Between one slide and the next, emit:
   \`{ "type": "divider", "slideId": "<the next slide's id>", "content": { "isSlideBreak": true } }\`

Without the dividers every block in a section lands on **one** slide, stacked on top
of each other and unreadable. A section with five slides has four dividers — one
between each pair, none before the first or after the last.

Keep a slide to roughly **one headline plus 4–6 bullets, or 2–3 short paragraphs**.
A slide that runs longer than that gets scaled down to fit the screen and becomes
hard to read from the back of a room. When a point needs more room, give it another
slide rather than a longer one.

### Sections
Use only these section ids, and only the ones you have content for:
${BOPPPS_SECTIONS.map((section) => bullet(`\`${section.id}\` — ${section.title}: ${section.purpose}`)).join('\n')}

### Block types
${blockTypeLines()}

### Worksheet question types
Each question is \`{ "type": ..., "title": "The question", "points": 0 }\`; \`mcq\` also
takes \`"options": ["A", "B"]\`. Valid types:
${worksheetTypeLines()}

### Not supported
${rejectedLines()}

### Content rules
- \`content\` is an HTML string. Use \`<p>\`, \`<ul>/<li>\`, \`<ol>/<li>\`, \`<strong>\`, \`<em>\`. Close every tag.
- Write for the learner, in the second person, not as notes to the instructor.
- **No square brackets anywhere.** \`[topic]\`, \`[insert example]\` and the like are rejected as
  unfilled placeholders — write the real thing or leave the field out.
- Do not invent statistics, citations, URLs or quotations. Leave media \`src\`/\`url\`
  fields empty for the instructor to fill, and describe what belongs there instead.`;
